
import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderPHP() {
  return <PHPComponent />;
}

function PHPComponent() {
  const navigate = useNavigate();

    return (
      <div className="transition-opacity duration-300">
    
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
          <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
          <FiChevronRight />
          <span>Agent Setup Guide</span>
          <FiChevronRight />
          <span>Technology Agent</span>
          <FiChevronRight />
          <span className="text-[#1b140d] dark:text-white font-medium">PHP</span>
        </nav>
    
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl font-extrabold">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
              PHP Agent Setup Guide
            </span>
          </h1>
          <p className="text-text-muted">
            This guide walks you through installing and configuring the Apmosys PHP Agent in your application.
          </p>
        </div>
    
        <section className="mb-6 scroll-mt-24" id="apmosys-agent">
          <h2 className="text-2xl font-bold text-text-main dark:text-white flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
              <span className="material-symbols-outlined text-[24px]">terminal</span>
            </span>
            PHP Agent Installation
          </h2>
    
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl p-6 shadow-subtle mb-6">
            <h2 className="text-lg font-bold text-text-main dark:text-white mb-4">Steps to Follow:-</h2>
    
            <ol className="space-y-6 list-decimal list-inside marker:text-primary marker:font-bold text-text-muted dark:text-gray-400 text-sm">
    
              <li>
                <p className="font-medium text-text-main dark:text-gray-200">Step 1: Install the agent extension</p>
    
                <div className="mt-2 ml-5 space-y-4">
                  <p>1.1 Check for PHP extensions directory</p>
                  <CodeBlock code={`php -i | grep extension_dir`} />
    
                  <p>1.2 Copy the provided .so file</p>
                  <CodeBlock code={`cp apmosys_agent.so /path/to/php/extensions/\n# Example:\ncp apmosys_agent.so /usr/lib/php/20230813/`} />
    
                  <p>1.3 Create apmosys_agent.log file</p>
                  <CodeBlock code={`mkdir -p /var/log/\ntouch /var/log/apmosys_agent.log\nchown www-data:www-data /var/log/apmosys_agent.log\nchmod 644 /var/log/apmosys_agent.log`} />
                </div>
              </li>
    
              <li>
                <p className="font-medium text-text-main dark:text-gray-200">Step 2: Prepare the PHP configuration file</p>
    
                <div className="mt-2 ml-5 space-y-4">
                  <p>2.1 Locate your PHP Configuration File (php.ini)</p>
                  <CodeBlock code={`php -i | grep "Loaded Configuration File"`} />
                  <p>Example: /etc/php/8.1/apache2/php.ini</p>
    
                  <p>2.2 Add Apmosys agent configuration</p>
                  <CodeBlock code={`apmosys_agent.service_name = {Service_Name}\napmosys_agent.instance_name = {Instance_Name}\n\n[apmosys_agent]\nextension=/path/to/apmosys_agent.so\napmosys_agent.enable = On\napmosys_agent.reporter_type = grpc\napmosys_agent.log_file = /var/log/apmosys_agent.log\napmosys_agent.log_level = ERROR\napmosys_agent.server_addr = netraaaop.ap2l.ai:11800\napmosys_agent.service_name = Application_Name\napmosys_agent.instance_name = Instance_Name\napmosys_agent.psr_logging_level = INFO\napmosys_agent.enable_zend_observer= Off`} />
                </div>
              </li> 
    
              <li>
                <p className="font-medium text-text-main dark:text-gray-200">Step 3: Load the agent at runtime</p>
                <div className="mt-2 ml-5">
                  <CodeBlock code={`sudo systemctl restart apache2`} />
                </div>
              </li>
    
              <li>
                <p className="font-medium text-text-main dark:text-gray-200">Step 4: Verify the Installation</p>
                <div className="mt-2 ml-5">
                  <p>Run your Application and check logs:</p>
                  <CodeBlock code={`/var/log/apmosys_agent.log`} />
                </div>
              </li>
    
              <li>
                <p className="font-medium text-text-main dark:text-gray-200">Optional: Enable Code Level Tracing</p>
    
                <div className="mt-2 ml-5 space-y-4">
                  <p>Manual Wrapping</p>
                  <p>
                    Wrap critical methods with spans using helper functions (start_span, end_span). Always mark errors if exceptions occur and release the span in a finally block.
                  </p>
    
                  <CodeBlock code={`<?php
    class Login {
        public function performLogin() {
            $span = start_span("Login::performLogin"); // Outer span
            try {
                // Method logic
    
                start_span("Login::connectDB"); // Nested span
                $this->db_connection = new mysqli(DB_HOST, DB_USER, DB_PASS, DB_NAME);
                end_span(); // End nested span
    
                // More logic here...
            } catch (Exception $e) {
                // Handle/log exception if needed
                error_log($e->getMessage());
                throw $e;
            } finally {
                end_span($span); // End outer span
            }
        }
    }`} />
                </div>
              </li>
    
            </ol>
          </div>
        </section>
    {/* Visualize Data CTA */}
    <section className="mb-10 flex justify-center">
      <button
        onClick={() => navigate("/homeservices")}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white
                   bg-gradient-to-r from-orange-500 to-red-600
                   hover:from-orange-600 hover:to-red-700
                   shadow-md hover:shadow-lg transition-all duration-300
                   dark:shadow-orange-900/30"
      >
        Visualize Data
        <FiChevronRight className="w-4 h-4" />
      </button>
    </section>
      </div>
    );
    }